import * as THREE from "three";
import { defineModel, type Part, type Variant } from "../../lib";

// A straight WWI trench segment running the FULL tile along Z so segments
// abut seamlessly. Since the ground plane is flat, the trench is built as two
// raised mud parapet banks (sandbag course on top) with a walkable channel
// between them at y=0 — never a hole below the floor. The channel is lined
// with vertical revetment planks and floored with timber duckboards; the
// "flooded" variant swaps the duckboards for a slab of murky water.
const MUD = "#5d4a33";
const DARK_MUD = "#46392a";
const SANDBAG = "#a08d5f";
const TIMBER = "#8b6438";
const PLANK = "#c9a36a";
const WATER = "#3e4a42";

const LEN = 1.0; // full tile along Z
const BANK_W = 0.26;
const BANK_H = 0.26;
const BANK_X = 0.32; // bank centres; channel is |x| < 0.19
const WALL_X = 0.185; // revetment plank walls (inner faces of the channel)

function bank(side: 1 | -1): Part[] {
  const x = side * BANK_X;
  const bags: Part[] = [-0.375, -0.125, 0.125, 0.375].map((z, i) => ({
    geometry: new THREE.BoxGeometry(0.26, 0.1, 0.23),
    color: SANDBAG,
    position: [x + side * 0.01, BANK_H + 0.05, z],
    rotation: [0, (i % 2 === 0 ? 1 : -1) * 0.08, 0],
  }));
  return [
    // Main parapet bank + a lower outer skirt so the profile reads as heaped earth
    { geometry: new THREE.BoxGeometry(BANK_W, BANK_H, LEN), color: MUD, position: [x, BANK_H / 2, 0] },
    { geometry: new THREE.BoxGeometry(0.14, 0.12, LEN), color: DARK_MUD, position: [x + side * 0.17, 0.06, 0] },
    ...bags,
    // Vertical revetment plank wall lining the channel side of the bank
    { geometry: new THREE.BoxGeometry(0.03, 0.26, LEN), color: PLANK, position: [side * WALL_X, 0.13, 0] },
    // Three timber revetment posts holding the wall
    ...[-0.36, 0, 0.36].map((z): Part => ({
      geometry: new THREE.BoxGeometry(0.045, 0.32, 0.055),
      color: TIMBER,
      position: [side * (WALL_X - 0.005), 0.16, z],
    })),
  ];
}

function channelFloor(): Part {
  return { geometry: new THREE.BoxGeometry(0.34, 0.02, LEN), color: DARK_MUD, position: [0, 0.01, 0] };
}

function duckboards(): Part[] {
  const slats: Part[] = [-0.4, -0.2, 0, 0.2, 0.4].map((z) => ({
    geometry: new THREE.BoxGeometry(0.3, 0.025, 0.12),
    color: PLANK,
    position: [0, 0.045, z],
  }));
  return [
    // Two timber runners with plank cross-slats on top
    { geometry: new THREE.BoxGeometry(0.05, 0.035, LEN), color: TIMBER, position: [-0.1, 0.025, 0] },
    { geometry: new THREE.BoxGeometry(0.05, 0.035, LEN), color: TIMBER, position: [0.1, 0.025, 0] },
    ...slats,
  ];
}

const variants: Variant[] = [
  {
    id: "straight",
    label: "Straight",
    description: "A dry trench with timber duckboards on the channel floor.",
    parts: () => [...bank(-1), ...bank(1), channelFloor(), ...duckboards()],
  },
  {
    id: "flooded",
    label: "Flooded",
    description: "The channel under a slab of murky standing water, one slat adrift.",
    parts: () => [
      ...bank(-1),
      ...bank(1),
      channelFloor(),
      { geometry: new THREE.BoxGeometry(0.34, 0.05, LEN), color: WATER, position: [0, 0.045, 0] },
      // A duckboard slat floating loose on the water
      { geometry: new THREE.BoxGeometry(0.28, 0.025, 0.12), color: PLANK,
        position: [0.02, 0.08, -0.22], rotation: [0, 0.35, 0] },
    ],
  },
];

export const trenchSegment = defineModel({
  id: "trench-segment",
  batchId: "wwi",
  author: "Fable 5",
  name: "Trench Segment",
  tags: ["wwi", "scenery", "trench", "fortification"],
  summary: "A straight trench segment: sandbagged parapet banks flanking a duckboarded channel.",
  footprint: 0.5,
  tiles: { w: 1, h: 1 },
  variants,
  animations: [],
});
